import React, { FC, useCallback, useMemo } from 'react';
import KeywordsInput from 'src/shared/Keywords/KeywordsInput';
import styles from './index.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-light-svg-icons';
import KeywordsAreaQuickFilter from './KeywordsAreaQuickFilter';
import { useApiTenders } from 'src/models/procurements/Tenders/hooks';
import { useTranslation } from 'react-i18next';
import { Keyword, fillKeywordDataWithDefaultValues } from '@tendium/prom-types';
import AdvancedSettingsQuickFilter from './AdvancedSettingsQuickFilter';
import { useMyFeaturesAndOwnership } from 'src/helpers';
import { GenericSearchDropdown, IGenericSearchDropdownProps } from 'src/shared';

export const KeywordsQuickFilter: FC<IGenericSearchDropdownProps> = props => {
  const { t } = useTranslation();
  const [{ vars }, updateSearchArgs] = useApiTenders();
  const fillKeywordsInfo = useMyFeaturesAndOwnership();

  const keywords = useMemo(() => vars.keywordArgs ?? [], [vars.keywordArgs]);

  const addKeyword = useCallback(
    (userInput?: string): void => {
      if (!userInput) return;
      const uniqFromInput = [
        ...new Set(
          userInput
            .split(',')
            .map(_ => _.trim())
            .filter(_ => !!_)
        )
      ];
      const uniqueFromKeywords = uniqFromInput.filter(input => !keywords?.map(_ => _.value).includes(input));

      if (!!uniqueFromKeywords.length) {
        const newKeywords: Keyword[] = uniqueFromKeywords.map(keyword => {
          return fillKeywordDataWithDefaultValues(
            { value: keyword },
            fillKeywordsInfo.myFeatureFlags,
            fillKeywordsInfo.myOwnership
          );
        });
        const newKeywordsState: Keyword[] = [...keywords, ...newKeywords];
        updateSearchArgs({ keywordArgs: newKeywordsState });
      }
    },
    [keywords, fillKeywordsInfo, updateSearchArgs]
  );

  const removeKeyword = useCallback(
    (title: string): void => {
      const newKeywordsState: Keyword[] = keywords.filter(keyword => keyword.value !== title);
      updateSearchArgs({ keywordArgs: newKeywordsState });
    },
    [keywords, updateSearchArgs]
  );

  const isChanged = useMemo(() => !!vars.keywordArgs?.length, [vars.keywordArgs?.length]);

  return (
    <GenericSearchDropdown
      {...props}
      label={t('ExpiringContracts.keywords')}
      overlay={
        <>
          <div className={styles.keywords}>
            <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
            <ul className={styles.keywordsList}>
              <KeywordsAreaQuickFilter keywords={keywords} removeKeyword={removeKeyword} />
              <KeywordsInput addKeyword={addKeyword} label={t('ExpiringContracts.addKeyword')} />
            </ul>
          </div>
          <div className={styles.advancedKeywords}>
            <AdvancedSettingsQuickFilter keywords={keywords} />
          </div>
        </>
      }
      overlayClassName={styles.keywordOverlay}
      isChanged={isChanged}
      special={props.special ? props.special : !vars.profileId && isChanged}
    />
  );
};

export default KeywordsQuickFilter;
